import { useCallback, useEffect, useState } from 'react';
import type { NodeEntry } from 'slate';
import { Editor, Range, Text } from '../core';

const UNFOCUSED_SELECTION_DECORATION = { unfocusedSelection: true } as const;

interface UseUnfocusedSelectionHighlightParams {
  editor: Editor;
  selection: Range | null;
  isEditorFocused: boolean;
}

interface UseUnfocusedSelectionHighlight {
  resetUnfocusedSelection: () => void;
  captureUnfocusedSelection: () => void;
  getUnfocusedSelectionDecorations: (entry: NodeEntry) => Range[];
}

/**
 * Tracks the editor's expanded selection while it is unfocused so the leaf renderer
 * (see `usePlugins`) can visually highlight it.
 */
export const useUnfocusedSelectionHighlight = ({
  editor,
  selection,
  isEditorFocused,
}: UseUnfocusedSelectionHighlightParams): UseUnfocusedSelectionHighlight => {
  const [unfocusedSelection, setUnfocusedSelection] = useState<Range | null>(null);

  useEffect(() => {
    if (isEditorFocused) {
      setUnfocusedSelection(null);
      return;
    }

    if (selection == null) {
      return;
    }

    setUnfocusedSelection(Range.isExpanded(selection) ? selection : null);
  }, [isEditorFocused, selection]);

  const resetUnfocusedSelection = useCallback(() => {
    setUnfocusedSelection(null);
  }, []);

  const captureUnfocusedSelection = useCallback(() => {
    setUnfocusedSelection(
      editor.selection != null && Range.isExpanded(editor.selection) ? editor.selection : null
    );
  }, [editor]);

  const getUnfocusedSelectionDecorations = useCallback(
    ([node, path]: NodeEntry): Range[] => {
      if (unfocusedSelection == null || !Text.isText(node)) {
        return [];
      }

      const nodeRange = Editor.range(editor, path);
      const intersection = Range.intersection(unfocusedSelection, nodeRange);

      if (intersection == null) {
        return [];
      }

      return [{ ...intersection, ...UNFOCUSED_SELECTION_DECORATION }];
    },
    [editor, unfocusedSelection]
  );

  return {
    resetUnfocusedSelection,
    captureUnfocusedSelection,
    getUnfocusedSelectionDecorations,
  };
};
